import React from "react";
import { Flex, Text, Box } from "rebass";
import { DropDown } from "./dropdown.jsx";
import { useRef } from "react";
import { isEmpty } from "lodash";

// INPUTS ELEMENTS
export const InputLabel = ({
	as = "div",
	color = "black",
	children,
	optional,
	helperText,
	...rest
}) => (
	<Flex as={as}>
		<Text
			as="label"
			fontWeight="600"
			fontSize={["0.9375rem", "0.9375rem", "1rem"]}
			color={color}
			mb="0.5rem"
			css={`
				line-height: 115.5%;
				letter-spacing: 0.02rem;
			`}
			{...rest}
		>
			{children}
			{optional && (
				<Text
					as="span"
					fontWeight="600"
					fontSize={["0.9375rem", "0.9375rem", "1rem"]}
					color={"#008EFA"}
					css={`
						letter-spacing: 0.02rem;
					`}
				>
					&nbsp;(Optional Field):
				</Text>
			)}
			{helperText && (
				<Text
					as="span"
					fontWeight="600"
					fontSize={["0.8rem", "0.8rem", "0.9375rem"]}
					color={"#008EFA"}
					css={`
						letter-spacing: 0.02rem;
					`}
				>
					&nbsp; {helperText}
				</Text>
			)}
		</Text>
	</Flex>
);

export const ErrorLabel = ({ color = "red", ...rest }) => (
	<Text
		fontSize={["0.9375rem", "0.9375rem", "1rem"]}
		color={color}
		my="10px"
		ml={3}
		css={`
			line-height: 115.5%;
			letter-spacing: 0.02rem;
		`}
		{...rest}
	/>
);

const RadioGroup = ({
	name,
	options = [],
	onChange,
	selectedValue,
	...rest
}) => (
	<Flex flexDirection="column">
		{options.map(({ label, value, showTextInput, disabledCondition }) => (
			<RadioButton
				key={label}
				label={label}
				name={name}
				value={value}
				selected={selectedValue === value}
				onChange={onChange}
				showTextInput={showTextInput}
				disabledCondition={disabledCondition}
				{...rest}
			/>
		))}
	</Flex>
);

const RadioButton = ({
	label,
	color = "#008EFA",
	name = "",
	value = "",
	selected = false,
	onChange = () => {},
	showTextInput = false,
	...rest
}) => (
	<Flex flexDirection="column">
		<Flex
			alignItems="center"
			my="0.3rem"
			css={`
				cursor: pointer;
				-webkit-user-select: none;
				-moz-user-select: none;
				-ms-user-select: none;
				user-select: none;
			`}
		>
			<Box
				as="input"
				id={value}
				color={color}
				bg={color}
				type="radio"
				name={name}
				value={value}
				width="2rem"
				mr="1rem"
				onChange={onChange}
				{...rest}
				css={`
					height: 2rem;
					margin-right: 10px;

					&:before {
						content: " ";
						width: 28px;
						height: 28px;
						position: absolute;
						background: transparent;
						border: 2px solid black;
						border-radius: 50%;
					}
					&:after {
						width: 20px;
						height: 20px;
						border-radius: 15px;
						top: 6.5px;
						left: 5.5px;
						position: relative;
						/* border: 2px solid ${color}; */
						background-color: ${color};
						content: ${selected ? "' '" : null};
						display: inline-block;
						visibility: visible;
						// border: 1px solid transparent;
					}
				`}
			/>
			<Text
				as="label"
				htmlFor={value}
				fontWeight={800}
				fontSize={"1.125rem"}
				css={`
					line-height: 1.3125rem;
				`}
			>
				{label}
			</Text>
		</Flex>
		{showTextInput && (
			<TextInput
				width={["100%", "100%", "180px"]}
				value={value}
				onChange={onChange}
				placeholder={label}
				disabled={!selected}
				ml={[0, 0, "3rem"]}
			/>
		)}
	</Flex>
);

const CheckButton = ({
	value,
	onChange,
	name,
	text = "",
	setFieldValue,
	...rest
}) => {
	const input = useRef(null);
	return (
		<Flex alignItems="center" flexDirection="row">
			<Box
				css={`
					position: relative;
					& label {
						background-color: #fff;
						border: 1px solid #ccc;
						border-radius: 50%;
						cursor: pointer;
						height: 28px;
						left: 0;
						position: absolute;
						top: 0;
						width: 28px;
					}

					& label:after {
						border: 2px solid #fff;
						border-top: none;
						border-right: none;
						content: "";
						height: 6px;
						left: 6px;
						opacity: 0;
						position: absolute;
						top: 7px;
						transform: rotate(-45deg);
						width: 12px;
					}

					& input[type="checkbox"] {
						visibility: hidden;
						z-index: 10;
					}

					& input[type="checkbox"]:checked + label {
						background-color: #008efa;
						border-color: #008efa;
					}

					& input[type="checkbox"]:checked + label:after {
						opacity: 1;
					}
				`}
				onClick={(e) => {
					setFieldValue(name, !input.current.checked, false);
					input.current.checked = !input.current.checked;
				}}
			>
				<Box
					as="input"
					type="checkbox"
					ref={input}
					value={value}
					checked={value}
					{...rest}
				/>
				<Box as="label" />
			</Box>
			<Text
				as="label"
				mt={"0.5rem"}
				ml={["2rem", "2rem", "1rem"]}
				fontWeight={800}
				fontSize={"1.125rem"}
				css={`
					line-height: 1.3125rem;
				`}
			>
				{text}
			</Text>
		</Flex>
	);
};

const TextInput = ({ disabledCondition = false, ...rest }) => {
	return (
		<Box
			as="input"
			css={`
				margin-top: 5px;
				height: 50px;
				border-radius: 6px;
				border: 1px solid lightgrey;
				padding: 15px;
				white-space: pre-wrap; /* Enables multi line messages */
				&:focus {
					border-radius: 0;
				}
				&:disabled {
					background-color: transparent;
					background-image: url("/static/icons/misc/stripe.svg");
					border: 1px dashed #008efa;
					&::placeholder {
						color: grey;
					}
				}
			`}
			disabled={disabledCondition}
			{...rest}
		/>
	);
};

// FORM ELEMENTS
const FormContainer = ({ width = "100%", children, onSubmit, id, ...rest }) => (
	<Flex
		id={id}
		as="form"
		width={width}
		flexDirection={"column"}
		onSubmit={onSubmit}
	>
		{children}
	</Flex>
);

const FormHeading = ({ title = "" }) => (
	<Text
		as="h2"
		fontWeight={800}
		width="100%"
		fontSize={["1.5rem", "1.5rem", "2.375rem"]}
		mb={["1rem", "1rem", "2rem"]}
	>
		{title}
	</Text>
);

export const FormSection = ({
	title = "",
	width = "100%",
	bg = "#F7F8FB",
	px = ["1.5625rem", "1.5625rem", "1.5625rem", "6.5rem"],
	py = ["2.5rem", "2.5rem", "3.75rem"],
	borderRadius = "6px",
	children,
	...rest
}) => (
	<Flex
		flexDirection="row"
		alignItems="flex-end" // These two props allow us to have thirds, that stick together
		width={width}
		bg={bg}
		css={`
			box-shadow: 0px 4px 100px rgba(0, 41, 73, 0.08);
			border-radius: ${borderRadius};
			flex-wrap: wrap;
		`}
		px={px}
		py={py}
		{...rest}
	>
		{title && <FormHeading title={title} />}
		{children}
	</Flex>
);

const FormItem = ({
	label,
	error = "",
	type = "text",
	containerWidth = "100%",
	labelProps,
	options,
	value,
	hide,
	optional,
	presetCondition,
	...rest
}) => {
	return (
		<Flex
			width={containerWidth}
			css={hide && "display: none"}
			flexDirection={"column"}
		>
			{label && (
				<InputLabel {...labelProps} optional={optional}>
					{label}
				</InputLabel>
			)}
			{type === "text" && <TextInput value={value} {...rest} />}
			{type === "textarea" && (
				<TextInput
					value={value}
					minHeight="150px"
					as="textarea"
					{...rest}
				/>
			)}
			{type === "dropdown" && (
				<DropDown value={value} options={options} {...rest} />
			)}
			{type === "radio" && (
				<RadioGroup
					name={label}
					options={options}
					selectedValue={value}
					{...rest}
				/>
			)}
			{type === "checkbox" && <CheckButton value={value} {...rest} />}
			{(error && (
				<ErrorLabel data-testid="error-label">{error}</ErrorLabel>
			)) || (
				<Box
					css={`
						height: 38px;
					`}
				/>
			)}
		</Flex>
	);
};

export class Form extends React.Component {
	static FormSection = FormSection;
	static FormItem = FormItem;

	render() {
		return (
			<FormContainer {...this.props}>{this.props.children}</FormContainer>
		);
	}
}

export const CombinedErrorHandler = ({
	GraphQLError,
	GraphQLErrors,
	FormikValidationErrors,
	stripeError,
	submitCount,
}) => (
	<>
		{GraphQLError && (
			<Box mb={10} color="red">
				<Text>{GraphQLError.message}</Text>
			</Box>
		)}
		{GraphQLErrors && (
			<Box mb={10} color="red">
				{GraphQLErrors.map(({ message, messages }, i) => (
					<>
						<Text key={i}>An error has occured: {message}</Text>
						{messages.map((m) => (
							<Text key={`${i}-message`}>
								An error has occured: {message}
							</Text>
						))}
					</>
				))}
			</Box>
		)}
		{!isEmpty(FormikValidationErrors) && submitCount > 0 && (
			<Box mb={10} color="red">
				<Text>Please review and correct the errors on this form.</Text>
			</Box>
		)}
		{stripeError && (
			<Box mb={10} color="red">
				<Text>Stripe Error: {stripeError}</Text>
			</Box>
		)}
	</>
);
